import type { UserDoc } from '../../shared/types';
import { CHAT_NAME, DAILY_NAME, previousDay, streakDayFor, streakOf, shownMedia } from '../../shared/constants';
import { avatarOf, urlFor, type DayPost } from './data';
import { isFamily, standing, type Member } from './family';
import { isGiviSnap } from './feed';
import type { TodayState } from './hooks';
import { go } from './router';
import { RecapCard } from './screens/Recap';
import type { RecapDoc } from '../../shared/recap';
import { Avatar, IconCamera, IconChat, IconMemory } from './ui';
import { Photo } from './Photo';

type Props = {
  uid: string;
  users: Record<string, UserDoc>;
  today: TodayState;
  /** Everyone else's frosted until you have posted yours, as on the GiviSnaps page. */
  locked: boolean;
  /** A GiviSnap is waiting on you. */
  promptOpen: boolean;
  windowEndsAt: number | null;
  onTakeGiviSnap: () => void;
  onSnap: () => void;
  /** Mems is in the pane, so its button is the way back to the chat. */
  onMems: boolean;
  /** The Sunday recap, while one is out. */
  recap?: RecapDoc | null;
  now?: number;
};

/** "3h 20m", "45m": how long a prompt has left, to the minute. */
export function leftUntil(endsAt: number | null, now: number): string | null {
  if (endsAt === null) return null;
  const minutes = Math.floor((endsAt - now) / 60_000);
  if (minutes <= 0) return null;
  if (minutes < 60) return `${minutes}m`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)}h ${rest}m` : `${Math.floor(minutes / 60)}h`;
}

/** "Mom", "Mom and Ethan", "Mom, Ethan and Isaac", "Mom, Ethan and 3 more". */
export function namesLine(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length <= 3) return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}

/**
 * The desktop's right side: the GiviSnaps page folded into tiles beside the
 * chat. A monitor has room for both, so the chat stays open on the left and
 * nothing needs a tab bar to reach.
 *
 * Who has posted and the streaks are worked out the way the GiviSnaps page
 * does it -- the day being counted, extras left out, test accounts left out
 * of the family's numbers -- so the two never disagree.
 */
export function Bento({
  uid,
  users,
  today,
  locked,
  promptOpen,
  windowEndsAt,
  onTakeGiviSnap,
  onSnap,
  onMems,
  recap = null,
  now = Date.now(),
}: Props) {
  const fired = Boolean(today.daily?.firedAt);
  const counting = streakDayFor(today.day, fired);
  const before = previousDay(counting);

  const answering = today.posts
    .filter(isGiviSnap)
    .filter((post) => post.day === counting)
    .sort((a, b) => millis(b) - millis(a));
  const answered = (id: string) => answering.some((post) => post.data.authorUid === id);
  const streakFor = (id: string) =>
    streakOf(users[id], counting, before, counting === today.day && answered(id));

  const family = Object.entries(users).filter(([, user]) => isFamily(user));
  const members: Member[] = family.map(([id]) => ({ id, streak: streakFor(id), posted: answered(id) }));
  const together = standing(members);
  const myStreak = streakFor(uid);
  const longest = members.filter((m) => together.crowned.has(m.id));
  const waitingOn = family.filter(([id]) => id !== uid && !answered(id));

  const left = leftUntil(windowEndsAt, now);
  const name = (id: string) => users[id]?.displayName ?? 'Someone';

  return (
    <aside className="bento" aria-label={`${DAILY_NAME}s`}>
      <section className="tile tile-prompt">
        <span className="tile-eyebrow">Today's {DAILY_NAME}</span>
        <span className="tile-row">
          <span className="tile-lead">
            {promptOpen ? 'Yours is waiting' : fired ? "You're in" : 'Not yet today'}
          </span>
          {promptOpen && (
            <button className="btn tile-btn" onClick={onTakeGiviSnap}>
              <IconCamera size={18} />
              Take yours
            </button>
          )}
        </span>
        <span className="tile-meta">
          {fired || promptOpen
            ? `${together.posted} of ${together.size} posted${left ? ` · ${left} left` : ''}`
            : 'It fires at a random time. You will get a buzz.'}
        </span>
      </section>

      <section className="tile tile-shots" aria-label={`Today's ${DAILY_NAME}s`}>
        {answering.length === 0 ? (
          <span className="tile-empty">
            <span className="empty-state-mark" aria-hidden="true">
              <IconCamera size={22} />
            </span>
            Nobody has posted yet.
          </span>
        ) : (
          answering.map((post) => {
            const author = post.data.authorUid;
            const frosted = locked && author !== uid;
            return (
              <button
                key={post.id}
                className="bento-shot"
                data-post={post.id}
                data-locked={frosted || undefined}
                aria-label={
                  frosted ? `${name(author)} posted. Post yours to see it.` : `${name(author)}'s ${DAILY_NAME}`
                }
                onClick={() => (frosted ? onTakeGiviSnap() : go(`daily/${post.day}/${post.id}`))}
              >
                {/* Frosted, not the photo blurred: a blurred photo has been fetched,
                    and anyone could unblur it. */}
                {frosted ? <span className="locked-shot" /> : <ShotThumb post={post} />}
                <span className="bento-shot-who">
                  <Avatar name={name(author)} photoURL={avatarOf(users[author])} size={22} />
                  <span className="bento-shot-name">{name(author)}</span>
                  <span className="bento-shot-time">{clock(millis(post))}</span>
                </span>
              </button>
            );
          })
        )}
      </section>

      <section className="tile">
        <span className="tile-eyebrow tile-eyebrow--quiet">Your streak</span>
        <span className="tile-big">{myStreak === 1 ? '1 day' : `${myStreak} days`}</span>
        {longest.length > 0 && (
          <span className="tile-meta">
            {longest.some((m) => m.id === uid)
              ? 'The longest in the family'
              : `Longest is ${namesLine(longest.map((m) => name(m.id)))} at ${longest[0].streak}`}
          </span>
        )}
      </section>

      <section className="tile">
        <span className="tile-eyebrow tile-eyebrow--quiet">Still to post</span>
        {waitingOn.length === 0 ? (
          <span className="tile-meta">Everyone else is in.</span>
        ) : (
          <>
            <span className="tile-faces">
              {waitingOn.slice(0, 6).map(([id, user]) => (
                <Avatar key={id} name={name(id)} photoURL={avatarOf(user)} size={34} />
              ))}
            </span>
            <span className="tile-meta">{namesLine(waitingOn.map(([id]) => name(id)))}</span>
          </>
        )}
      </section>

      {recap && (
        <section className="tile tile-recap">
          <RecapCard recap={recap} />
        </section>
      )}

      <div className="bento-actions">
        <button className="bento-action" onClick={onSnap}>
          <IconCamera size={18} />
          Snap
        </button>
        <button className="bento-action" onClick={() => go(onMems ? 'chat' : 'memories')}>
          {onMems ? <IconChat size={18} /> : <IconMemory size={18} />}
          {onMems ? CHAT_NAME : 'Mems'}
        </button>
      </div>
    </aside>
  );
}

function ShotThumb({ post }: { post: DayPost }) {
  const data = post.data;
  if (data.video) {
    return <video className="bento-shot-media" src={urlFor(data.video)} muted playsInline preload="metadata" />;
  }
  return data.back ? <Photo className="bento-shot-media" src={urlFor(shownMedia(data.back))} preview={data.back.blur} alt="" loading="lazy" /> : null;
}

function millis(post: DayPost): number {
  return (post.data.createdAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0;
}

function clock(ms: number): string {
  return ms ? new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
}
